import { LegacyRef, ReactNode, useEffect, useState, useRef } from "react";
import * as Sentry from "@sentry/react";

// DEPENDENCIES
import QRCode from "qrcode";
import axios from "axios";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  DocumentData,
  DocumentReference,
  Firestore,
  Timestamp,
  deleteField,
  getFirestore,
  onSnapshot,
  setDoc,
} from "@firebase/firestore";

// UTILS
import useMobileLayout from "../../hooks/useMobileLayout";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { logAnalyticsAndHotjarEvent } from "../../main";
import {
  IDENTITY_MISMATCH_ERROR,
  isLivenessFeedbackVisible,
  livenessStep,
} from "./utils/utils";
import { getLivenessSessionId } from "../../connectors/connectors";
import { closeModal, openModal } from "../../reducers/modalReducer";
import { showToastMessage } from "@allaria-tech/matter/ToastMessage";
import {
  onUpdateLivenessIsValid,
  onUpdateLivenessState,
  onUpdateSessionId,
} from "../../reducers/livenessReducer";
import {
  onUpdateExpiredSessionResume,
  onUpdateStep,
  onUpdateSubStep,
} from "../../reducers/onboardingDataReducer";
import {
  onUpdateIsNavigating,
  onUpdatePrimaryButton,
  onUpdateSecondaryButton,
  onUpdateTertiaryButton,
} from "../../reducers/buttonsReducer";
import {
  onUpdateOnClose,
  onUpdateQrImage,
  onUpdateScannedQr,
  onUpdateSuccessMessage,
} from "../../reducers/qrReducer";
import useForm from "../../hooks/useForm";
import useRemoteParams from "../../hooks/useRemoteParams";
import useTheme from "../../hooks/useTheme";
import useCameraRelease from "../../hooks/useCameraRelease";
import { FIRESTORE_KYC_DOC_TTL_DAYS } from "../../constants";

// TYPES
import { LivenessData } from "./interfaces/types";
import {
  ComponentProps,
  ErrorResponse,
  ValidationErrorResponse,
} from "../../types";

// COMPONENTS
import Layout from "../Layout/Layout";
import Container from "../Container/Container";
import FormContainer from "../FormContainer/FormContainer";
import QRModal from "../QRModal/QRModal";
import { Player } from "@lottiefiles/react-lottie-player";
import { ButtonGroup } from "@allaria-tech/matter/ButtonGroup";
import { TextField } from "@allaria-tech/matter/TextField";
import MinimumRequirementsModal from "./MinimumRequirementsModal/MinimumRequirementsModal";
import Liveness from "./Liveness";

import { qrStyle } from "../../utils";
import {
  reloadDraftAfterExpiredSession,
  waitForFirebaseLogin,
} from "../../app/utils";

import styles from "./LivenessValidationCard.module.scss";

interface LivenessValidationCardProps extends ComponentProps {
  getLivenessRef: ({
    db,
    uuid,
    email,
  }: {
    db: Firestore;
    uuid: string;
    email: string;
  }) => DocumentReference<DocumentData, DocumentData>;
  showCopyLink?: boolean;
  onCustomGetSessionId?: (
    uuid: string,
  ) => Promise<{ data: { sessionId: string } }>;
  onCustomValidateFace?: (
    uuid: string,
    sessionId: string,
    livenessRef: DocumentReference<DocumentData, DocumentData>,
  ) => Promise<void>;
  onCustomValidateFaceError?: (
    error: unknown,
    reachedAttemptsLimit: boolean,
  ) => void;
  /** Shown above the tips on the desktop start screen. */
  description?: ReactNode;
  /** Adds a "Cerrar" button that calls it while the capture has not started. */
  onClose?: () => void;
  testId?: string;
}

type SessionIdResponse = { data: { sessionId: string } };

let pendingSessionRequest: Promise<SessionIdResponse> | null = null;

const requestSessionId = (request: () => Promise<SessionIdResponse>) => {
  if (!pendingSessionRequest)
    pendingSessionRequest = request().finally(() => {
      pendingSessionRequest = null;
    });
  return pendingSessionRequest;
};

const LivenessValidationCard = ({
  formik,
  getLivenessRef,
  showCopyLink,
  onCustomGetSessionId,
  onCustomValidateFace,
  onCustomValidateFaceError,
  description,
  onClose,
  testId = "liveness-validation-card",
}: LivenessValidationCardProps) => {
  const {
    cardRoot,
    container,
    formContainer,
    copyLinkContainer,
    externalLink,
    withQr,
    buttonsContainer,
  } = styles;

  const { handleBack, handleContinue, continueToPreconfirmation } =
    useForm(formik);

  const expiredSessionResume = useAppSelector(
    (state) => state.onboardingData.expiredSessionResume,
  );

  const resumeAfterExpiredSession = async () => {
    if (!expiredSessionResume) return;
    dispatch(onUpdateExpiredSessionResume(null));

    if (expiredSessionResume.reloadDraft) {
      dispatch(onUpdateIsNavigating(true));
      const reloaded = await reloadDraftAfterExpiredSession().catch((error) => {
        Sentry.captureException(error);
        return false;
      });
      dispatch(onUpdateIsNavigating(false));
      if (reloaded) return;
    }

    if (expiredSessionResume.preconfirmation) {
      continueToPreconfirmation();
      return;
    }
    dispatch(onUpdateStep(expiredSessionResume.step));
    dispatch(onUpdateSubStep(expiredSessionResume.subStep));
  };

  const handleContinueLiveness = async () => {
    if (onboardingType === "DIGITAL") {
      await setDoc(
        livenessRef,
        { remoteToken: deleteField() },
        { merge: true },
      );
    }
    if (expiredSessionResume) return resumeAfterExpiredSession();
    handleContinue();
  };

  const [error, setError] = useState<string | null>(null);

  const playerRef: LegacyRef<Player> | undefined = useRef(null);

  const dispatch = useAppDispatch();

  const { livenessState, sessionId } = useAppSelector(
    (state) => state.liveness,
  );
  const { hideSupportButton } = useAppSelector((state) => state.activeTemplate);
  const theme = useTheme();

  const [searchParams] = useSearchParams();
  const success = searchParams.get("success") === "true";

  // Remote
  const location = useLocation();
  const isRemoteStep = location.pathname.includes("identidad");

  const { remoteUrlParams, values } = useRemoteParams();
  const { continueWithQR, uuid, email, onboardingType } = values;
  const { isCrypto, isMobile } = useMobileLayout();

  const livenessLink = `${
    window.location.origin
  }/identidad?${remoteUrlParams.toString()}`;

  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);

  const userAgentData = window.navigator.userAgent || "Unknown";

  const [livenessData, setLivenessData] = useState<LivenessData>({
    email,
    scannedQR: false,
    isValid: false,
    userAgentData,
  });

  // Firestore
  const db = getFirestore();
  const livenessRef = getLivenessRef({ db, uuid: uuid, email: email });

  const { scannedQR, isValid } = livenessData;

  const [reachedAttemptsLimit, setReachedAttemptsLimit] =
    useState<boolean>(false);
  const [isIdentityMismatch, setIsIdentityMismatch] = useState<boolean>(false);

  // Stop camera when detection is not active
  useCameraRelease(livenessState === "DETECTOR");

  const handleError = (error?: string) => {
    if (error === IDENTITY_MISMATCH_ERROR) {
      setReachedAttemptsLimit(true);
      setIsIdentityMismatch(true);
    }
    setError(error ?? "FAILED_VALIDATION");
    dispatch(onUpdateLivenessState("NOT_VALIDATED"));
  };

  const handleOpenQr = () => {
    dispatch(onUpdateScannedQr(false));
    dispatch(openModal({ name: "modal-qr" }));
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(livenessLink);
    showToastMessage("Link copiado al portapapeles.", {
      containerId: "layoutPage",
      type: "SUCCESS",
    });
  };

  const primaryRemoteButton = {
    show: !reachedAttemptsLimit && !["VALIDATED", null].includes(livenessState),
    text:
      livenessState === "NOT_VALIDATED"
        ? "Reintentar captura"
        : "Comenzar captura",
    onClick: () => {
      setError(null);
      if (livenessState !== "NOT_VALIDATED")
        logAnalyticsAndHotjarEvent("identity_verification_started");
      dispatch(
        onUpdateLivenessState(
          livenessState === "INITIAL" ? "DETECTOR" : "INITIAL",
        ),
      );
      if (livenessState === "NOT_VALIDATED") dispatch(onUpdateSessionId(""));
    },
    loading: !sessionId,
    disabled:
      error === "CAMERA_FRAMERATE_ERROR" ||
      (!!livenessState && livenessState === "DETECTOR"),
  };

  const secondaryRemoteButton = {
    show:
      !isMobile &&
      livenessState &&
      ["INITIAL", "NOT_VALIDATED", "DETECTOR"].includes(livenessState),
    text: "Validar con mi teléfono",
    onClick: handleOpenQr,
    disabled: isIdentityMismatch,
  };

  const livenessValidation = (
    <Liveness
      uuid={uuid}
      sessionId={sessionId}
      livenessRef={livenessRef}
      isLoadingData={isLoadingData}
      livenessState={livenessState}
      livenessData={livenessData}
      reachedAttemptsLimit={reachedAttemptsLimit}
      isRemoteStep={isRemoteStep}
      continueWithQR={continueWithQR}
      isMobile={isMobile}
      error={error}
      handleError={handleError}
      playerRef={playerRef}
      userAgentData={userAgentData}
      onboardingType={onboardingType}
      onCustomValidateFace={onCustomValidateFace}
      onCustomValidateFaceError={onCustomValidateFaceError}
      description={description}
      testId={`${testId}-liveness`}
    />
  );

  useEffect(() => {
    if (!isValid || livenessState === "VALIDATED") return;

    const finishValidation = async () => {
      dispatch(closeModal());
      setDoc(livenessRef, { scannedQR: false }, { merge: true });
      logAnalyticsAndHotjarEvent("step_identity_completed_pf");
      logAnalyticsAndHotjarEvent("identity_verification_completed");

      dispatch(onUpdateLivenessState(continueWithQR ? "VALIDATED" : null));
      setTimeout(() => {
        playerRef.current?.play();
      }, 1000);
    };

    finishValidation();
  }, [isValid, livenessState]);

  useEffect(() => {
    if (
      livenessState &&
      ["DETECTOR", "NOT_VALIDATED", "CAMERA_ACCESS_REQUIRED"].includes(
        livenessState,
      )
    ) {
      dispatch(onUpdateSessionId(""));
      dispatch(onUpdateLivenessState(null));
    }
  }, []);

  useEffect(() => {
    error &&
      (error === "CAMERA_ACCESS_ERROR"
        ? dispatch(onUpdateLivenessState("CAMERA_ACCESS_REQUIRED"))
        : dispatch(onUpdateLivenessState("NOT_VALIDATED")));
  }, [error]);

  useEffect(() => {
    if (livenessState && !isRemoteStep) {
      if (["INITIAL", "NOT_VALIDATED"].includes(livenessState)) {
        if (!isMobile) {
          dispatch(
            onUpdateSecondaryButton({
              text: "Validar con mi teléfono",
              onClick: () => {
                setError(null);
                setDoc(livenessRef, { scannedQR: false }, { merge: true });
                handleOpenQr();
              },
              // Only the crypto onboarding closes the door to the phone once
              // the attempts limit is reached. The rest keep the button enabled,
              // which is how they have always behaved.
              disabled:
                isIdentityMismatch || (isCrypto && reachedAttemptsLimit),
              show: true,
            }),
          );
        } else {
          dispatch(
            onUpdateSecondaryButton({
              text: "Volver",
              disabled: false,
              isLoading: false,
              onClick: handleBack,
              show: false,
            }),
          );
          onUpdateLivenessState(null);
        }
      }

      if (onClose && livenessState !== "DETECTOR") {
        dispatch(
          onUpdateTertiaryButton({
            text: "Cerrar",
            variant: "secondary",
            disabled: false,
            isLoading: false,
            onClick: onClose,
            show: true,
          }),
        );
      } else if (isMobile) {
        dispatch(onUpdateTertiaryButton({ show: false }));
      } else {
        dispatch(
          onUpdateTertiaryButton({
            text: "Volver",
            disabled: false,
            isLoading: false,
            onClick: handleBack,
            show: false,
          }),
        );
        onUpdateLivenessState(null);
      }

      switch (livenessState) {
        case "INITIAL":
          dispatch(
            onUpdatePrimaryButton({
              text: "Comenzar captura",
              disabled: false,
              isLoading: !sessionId,
              onClick: () => dispatch(onUpdateLivenessState("DETECTOR")),
              show: true,
            }),
          );
          break;

        case "DETECTOR":
          dispatch(
            onUpdatePrimaryButton({
              disabled: true,
              show: true,
            }),
          );
          break;

        case "NOT_VALIDATED":
        case "CAMERA_ACCESS_REQUIRED":
          dispatch(
            onUpdatePrimaryButton({
              text: "Reintentar captura",
              isLoading: false,
              onClick: () => {
                setError(null);
                dispatch(onUpdateSessionId(""));
                dispatch(onUpdateLivenessState("INITIAL"));
              },
              disabled: false,
              show: true,
            }),
          );

          reachedAttemptsLimit &&
            dispatch(
              onUpdatePrimaryButton({
                disabled: true,
                show: true,
              }),
            );
          break;

        case "VALIDATED":
          dispatch(
            onUpdatePrimaryButton({
              text: "Continuar",
              disabled: false,
              isLoading: false,
              type: "button",
              onClick: handleContinueLiveness,
              show: true,
            }),
          );
          dispatch(
            onUpdateSecondaryButton({
              text: "Volver",
              disabled: false,
              isLoading: false,
              onClick: handleBack,
              show: false,
            }),
          );
          dispatch(onUpdateTertiaryButton({ show: false }));

          onUpdateLivenessState(null);
          break;
        default:
          break;
      }
    }
  }, [
    livenessState,
    sessionId,
    reachedAttemptsLimit,
    isIdentityMismatch,
    isMobile,
    isCrypto,
  ]);

  // The buttons are merged in the store: "Cerrar"'s variant must not stay on
  // the next step's tertiary button.
  useEffect(
    () => () => {
      onClose && dispatch(onUpdateTertiaryButton({ variant: undefined }));
    },
    [],
  );

  useEffect(() => {
    let isUnmounted = false;

    livenessState === "INITIAL" &&
      (isRemoteStep || !sessionId) &&
      requestSessionId(() =>
        onCustomGetSessionId
          ? onCustomGetSessionId(uuid)
          : getLivenessSessionId(),
      )
        .then((res) => {
          if (isUnmounted) return;
          const sessionId = res.data.sessionId;
          dispatch(onUpdateSessionId(sessionId));
        })
        .catch((error: ErrorResponse | ValidationErrorResponse) => {
          if (isUnmounted) return;
          console.log(error);
          if (
            axios.isAxiosError(error) &&
            error.response?.status === 409 &&
            error.response.data?.code === "MAX_ATTEMPTS_REACHED"
          ) {
            setReachedAttemptsLimit(true);
            handleError("MAX_ATTEMPTS_ERROR");
            return;
          }
          handleError("SESSION_ID_ERROR");
        });
    if (livenessState === "VALIDATED") {
      dispatch(
        onUpdatePrimaryButton({
          text: "Continuar",
          disabled: false,
          isLoading: false,
          type: "button",
          onClick: handleContinueLiveness,
          show: true,
        }),
      );
    }

    return () => {
      isUnmounted = true;
    };
  }, [livenessState]);

  useEffect(() => {
    if (
      !isLoadingData &&
      !isValid &&
      // Running again on INITIAL is what propagates a limit reached on the
      // phone back to the desktop. Only crypto needs it; the rest keep reacting
      // from a null state alone, as they did before.
      (!livenessState || (isCrypto && livenessState === "INITIAL"))
    ) {
      dispatch(
        onUpdateLivenessState(
          reachedAttemptsLimit ? "NOT_VALIDATED" : "INITIAL",
        ),
      );
    }
  }, [isLoadingData, reachedAttemptsLimit, livenessState, isCrypto]);

  // Closes the QR left open on the desktop when the limit is reached from the
  // phone. Scoped to crypto so the other onboardings keep their modals: this
  // dispatch clears whichever modal is open, not just the QR one.
  useEffect(() => {
    if (isCrypto && reachedAttemptsLimit) dispatch(closeModal());
  }, [isCrypto, reachedAttemptsLimit, dispatch]);

  useEffect(() => {
    let unsub: (() => void) | undefined;
    let cancelled = false;

    waitForFirebaseLogin()
      .then(async () => {
        if (!expiredSessionResume || isRemoteStep) return;
        dispatch(onUpdateLivenessIsValid(false));
        dispatch(onUpdateSessionId(""));
        dispatch(onUpdateLivenessState(null));
        await setDoc(livenessRef, { isValid: false }, { merge: true });
      })
      .catch(Sentry.captureException)
      .then(() => {
        if (cancelled) return;
        unsub = subscribeToLivenessDoc();
      });

    return () => {
      cancelled = true;
      unsub?.();
    };
  }, []);

  const subscribeToLivenessDoc = () =>
    onSnapshot(livenessRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data() as LivenessData;
        setLivenessData((prevState) => ({
          ...prevState,
          ...data,
        }));
        dispatch(onUpdateLivenessIsValid(!!data.isValid));
        setIsLoadingData(false);
      } else {
        // `expiresAt` lo consume la Cloud Function programada `cleanupRenaper`, que borra
        // los documentos vencidos (drafts abandonados) con los datos que pudieran tener.
        // Firestore rechaza el documento entero si un campo es `undefined`, y en el
        // celular `gender` lo es.
        setDoc(
          livenessRef,
          {
            ...Object.fromEntries(
              Object.entries(livenessData).filter(
                ([, value]) => value !== undefined,
              ),
            ),
            expiresAt: Timestamp.fromMillis(
              Date.now() + FIRESTORE_KYC_DOC_TTL_DAYS * 24 * 60 * 60 * 1000,
            ),
          },
          { merge: true },
        );
      }
    });

  useEffect(() => {
    if (continueWithQR && !isLoadingData) {
      logAnalyticsAndHotjarEvent("qr_code_scanned_selfie");
      setDoc(livenessRef, { scannedQR: true }, { merge: true });
    }
  }, [continueWithQR, isLoadingData]);

  useEffect(() => {
    if (isCrypto) {
      const { token } = values;
      setDoc(livenessRef, { remoteToken: token }, { merge: true });
      return () => {
        setDoc(livenessRef, { remoteToken: deleteField() }, { merge: true });
      };
    }
  }, []);

  // Set QR link
  useEffect(() => {
    QRCode.toDataURL(livenessLink.concat(`&withQR=true`), qrStyle(theme)).then(
      (img) => dispatch(onUpdateQrImage(img)),
    );
    dispatch(
      onUpdateSuccessMessage(
        "Ya podés validar tu identidad desde el teléfono.",
      ),
    );
    dispatch(
      onUpdateOnClose(() =>
        setDoc(livenessRef, { scannedQR: false }, { merge: true }),
      ),
    );
  }, [livenessData]);

  useEffect(() => {
    dispatch(onUpdateScannedQr(scannedQR));
  }, [scannedQR]);

  return (
    <section className={cardRoot} data-testid={testId}>
      <QRModal />
      <MinimumRequirementsModal />
      {isRemoteStep ? (
        <Layout
          hideStepper
          hideSupportButton={hideSupportButton}
          className={continueWithQR ? withQr : ""}
          testId={`${testId}-layout`}
        >
          <Container className={container}>
            <FormContainer
              currentStep={livenessStep}
              currentSubStep={1}
              hideFormHeader={
                isLivenessFeedbackVisible({
                  livenessState,
                  isValid,
                }) || isMobile
              }
              classNameFormContainer={formContainer}
              className={`${isRemoteStep ? externalLink : ""}`}
            >
              {livenessValidation}
            </FormContainer>
          </Container>
          {!success &&
            (primaryRemoteButton.show || secondaryRemoteButton.show) && (
              <ButtonGroup
                container
                containerClassName={buttonsContainer}
                secondary={
                  secondaryRemoteButton.show
                    ? {
                        text: secondaryRemoteButton.text,
                        onClick: secondaryRemoteButton.onClick,
                        isDisabled: secondaryRemoteButton.disabled,
                        testId: `${testId}-secondary`,
                      }
                    : undefined
                }
                primary={
                  primaryRemoteButton.show
                    ? {
                        text: primaryRemoteButton.text,
                        onClick: primaryRemoteButton.onClick,
                        isLoading: primaryRemoteButton.loading,
                        isDisabled: primaryRemoteButton.disabled,
                        testId: `${testId}-primary`,
                      }
                    : undefined
                }
              />
            )}
        </Layout>
      ) : (
        <>
          {livenessValidation}
          {showCopyLink && livenessState === "INITIAL" && (
            <div
              className={copyLinkContainer}
              data-testid={`${testId}-copy-link`}
            >
              <h4>
                O compartí el siguiente link y esperá a que termine la
                validación.
              </h4>
              <TextField
                variant="url"
                value={livenessLink}
                onClick={handleCopyLink}
                testId={`${testId}-link`}
              />
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default LivenessValidationCard;
